import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, tableDividerColumnWithAgg, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { formatNumIgnoreNullAndUndefined, formatNumTwoDig, formatPercent, formatPercentWithIcon } from 'app/utils';
import { simpleFitCell } from './FitnessTrackerListe';
import { useHistory, useLocation } from 'react-router';
import routes from 'routes';
import { FitnessTrackerGraphics } from './FitnessTrackerGraphics';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import { GupHistoryChart } from './FitnessTrackerGupHistory';

interface IFitnessTrackerGUP {
	showMultiGPfields?: boolean;
}

export const FitnessTrackerGUP = observer((props: IFitnessTrackerGUP) => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [filteredAgtIds, setFilteredAgtIds] = useState<number[]>([]);

	const [selectedAgtId, setSelectedAgtId] = useState<number | undefined>(undefined);
	const history = useHistoryWithQuery();
	const location = useLocation();

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		if (props.showMultiGPfields) {
			cols.push({
				label: 'GP: Min 25 Bew',
				path: `gupGpCount`,
				sortBy: `gupGpCount`,
				agg: [
					{
						aggType: AggregrationType.sum,
					},
				],
			});
		}

		// GOOGLE
		cols.push({
			label: 'Ranking',
			path: 'gup.rank',
			sortBy: 'gup.rank',
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			format: formatNumTwoDig,
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercent(val, 1),
			},
		});
		cols.push({
			label: 'GUP Neue Bewertungen',
			path: `neueBewertung.ze`,
			sortBy: `neueBewertung.ze`,
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			render(row) {
				return simpleFitCell(row.data.neueBewertung);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'GUP Bewertungen Durchschnitt',
			path: `bewAvg.ze`,
			sortBy: `bewAvg.ze`,
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.gupBewAvg);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		cols.push({
			label: 'GUP Bilder/Videos',
			path: `imgCount.ze`,
			sortBy: `imgCount.ze`,
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.imgCount);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});
		// cols.push({
		// 	label: 'GUP Fragen + Anworten',
		// 	path: `qCount.ze`,
		// 	sortBy: `qCount.ze`,
		// 	cellModifier: 'bg-green',
		// 	labelModifier: 'bg-green',
		// 	render(row) {
		// 		return simpleFitCell(row.data.qCount);
		// 	},
		// 	agg: {
		// 		aggType: AggregrationType.avg,
		// 		format: (val) => formatPercentWithIcon(val, 1),
		// 	},
		// });
		cols.push({
			label: 'Durchschnitt',
			path: `gupBewAvg.ze`,
			sortBy: `gupBewAvg.ze`,
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			render(row) {
				return <>{formatPercent(row.data.gupBewAvg.ze)}%</>;
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
			},
		});

		cols.push(tableDividerColumnWithAgg);

		// PLUS: Social Media
		cols.push({
			label: (
				<>
					<span className="no-wrap">Sichten ✚</span>
					<br />
					Facebook
				</>
			),
			path: 'sm.currFb.views',
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			format: formatNumIgnoreNullAndUndefined,
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					Instagram
				</>
			),
			path: 'sm.currInsta.views',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			format: formatNumIgnoreNullAndUndefined,
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					TikTok
				</>
			),
			path: 'sm.currTiktok.views',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			format: formatNumIgnoreNullAndUndefined,
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					LinkedIn
				</>
			),
			path: 'sm.currLinkedin.views',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			format: formatNumIgnoreNullAndUndefined,
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<span className="no-wrap">Eigene Posts ✚</span>
					<br />
					Facebook
				</>
			),
			path: 'fbEigenePosts.ergebnis',
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			render(row) {
				return simpleFitCell(row.data.fbEigenePosts);
			},
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					Instagram
				</>
			),
			path: 'instaEigenePosts.ergebnis',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.instaEigenePosts);
			},
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					TikTok
				</>
			),
			path: 'tikTokPosts.ergebnis',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.tikTokPosts);
			},
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					LinkedIn
				</>
			),
			path: 'linkedInPosts.ergebnis',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.linkedInPosts);
			},
			agg: {
				aggType: AggregrationType.avg,
				aggIf: (val: any) => val > 0,
			},
		});

		cols.push({
			label: (
				<>
					<span className="no-wrap">Fans & Follower ✚</span>
					<br /> Facebook
				</>
			),
			path: 'fbFanAnzahl.ergebnis',
			sortBy: 'fbFanAnzahl.ergebnis',
			sortType: 'number',
			cellModifier: 'border-left-green bg-green',
			labelModifier: 'border-left-green bg-green',
			render(row) {
				return simpleFitCell(row.data.fbFanAnzahl);
			},
			agg: {
				aggType: AggregrationType.avg,
				format: (val) => formatPercentWithIcon(val, 1),
				path: 'fbFanAnzahl.ze',
				aggIf: (val: any) => val > 0,
			},
		});
		cols.push({
			label: (
				<>
					<br />
					Instagram
				</>
			),
			path: 'instaFollower.ergebnis',
			sortBy: 'instaFollower.ergebnis',
			sortType: 'number',
			cellModifier: 'bg-green',
			labelModifier: 'bg-green',
			render(row) {
				return simpleFitCell(row.data.instaFollower);
			},
			agg: {
				aggType: AggregrationType.avg,
				path: 'instaFollower.ze',
				format: (val) => formatPercentWithIcon(val, 1),
				aggIf: (val: any) => val > 0,
			},
		});

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.onRowClick = (row) => {
			const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'gup', row.data.agt.agtId.toString());
			history.pushWithQuery(p);
		};

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			// Store all agency IDs for aggregated chart view
			if (items.length > 0) {
				const agtIds = items.map((item) => item.agt.agtId);
				setFilteredAgtIds(agtIds);
			} else {
				setFilteredAgtIds([]);
			}
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	useEffect(() => {
		// das hier ginge auch nicer, via treeNode Model und openByPath
		const lastPathElement = location.pathname.split('/').pop();
		let agtId;
		if (typeof lastPathElement === 'string') {
			agtId = parseInt(lastPathElement);
		}
		if (agtId && !isNaN(agtId)) {
			setSelectedAgtId(agtId);
		} else {
			setSelectedAgtId(undefined);
		}
	}, [location]);

	const onClose = () => {
		const p = routes.BERICHTE.getPath('bestand', 'adz', 'fit', 'gup');
		history.pushWithQuery(p);
	};

	return (
		<>
			<div className="table-container">
				{!hasItems ? (
					<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
				) : (
					<>
						{!selectedAgtId && filteredAgtIds.length > 0 && (
							<div className="pad-1rem pad-bottom-2rem  is-sticky">
								<GupHistoryChart agtIds={filteredAgtIds} />
							</div>
						)}
						{selectedAgtId && <FitnessTrackerGraphics agtId={selectedAgtId} view="gup" onClose={onClose} />}
						<Table stickyHeader={true} stickyFooter={true} tm={tm}></Table>
					</>
				)}
			</div>
		</>
	);
});
